import { useTranslation } from "@/app/i18n/client";
import { CaseCategory, CaseStudy } from "@/sanity.types";
import { urlFor } from "@/sanity/lib/image";
import clsx from "clsx";
import { AnimatePresence, motion, useInView } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { Dispatch, SetStateAction, useEffect, useRef } from "react";

const CaseTile = ({
	tileData,
	isActive,
	index,
	lng,
	setHoveredIndex,
}: {
	tileData: CaseStudy;
	isActive: boolean;
	index: number;
	lng: string;
	setHoveredIndex: Dispatch<SetStateAction<number | null>>;
}) => {
	const { t } = useTranslation(lng);
	const tileRef = useRef<HTMLDivElement>(null);
	const isInView = useInView(tileRef, { margin: "-49% 0% -49% 0%" });

	useEffect(() => {
		if (isInView) {
			setHoveredIndex(index);
		}
	}, [isInView]);

	return (
		<Link prefetch={false} href={`/${lng}/case-studies/${tileData.slug?.current}`}>
			<div
				ref={tileRef}
				className={clsx(
					isActive && "shadow-blueish",
					"flex aspect-[61/29] w-full flex-col justify-between rounded-[1.25rem] border border-gray-300 bg-white-80 p-10 backdrop-blur-[30px] sm:aspect-[14/10] xs:aspect-[17/14] xs:p-5",
				)}
			>
				<h5 className="w-7/12 font-secondary text-h5 xs:w-full xs:text-h5-xs">{tileData.title}</h5>
				<div
					className={clsx(
						isActive ? "w-44 bg-blue xs:w-28" : "w-11 bg-gray-300",
						"relative flex h-11 items-center justify-end rounded-[1.875rem] text-white duration-500 sm:h-10",
					)}
				>
					<p className={clsx(isActive ? "pr-7 xs:opacity-0" : "pr-3.5 xs:opacity-100", "text-right duration-500")}>→</p>
					<AnimatePresence>
						{isActive && (
							<motion.p
								key={"cta" + index}
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.5 }}
								className="absolute left-7 whitespace-nowrap text-base xs:left-1/2 xs:-translate-x-1/2 xs:text-base-s"
							>
								{t("caseStudies.seeMore")}
							</motion.p>
						)}
					</AnimatePresence>
				</div>
				<div className="absolute bottom-6 right-8 aspect-[8375/10750] h-[60%] xs:bottom-0 xs:right-0">
					<AnimatePresence mode="popLayout">
						{!isActive ? (
							<motion.div
								key={"icon" + index}
								className="relative h-full w-full"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.5 }}
							>
								<Image
									src={urlFor(tileData.category?.icon).url()}
									fill
									style={{ objectFit: "contain" }}
									alt={(tileData.category as unknown as CaseCategory).title || ""}
									sizes="(max-width: 600px) 30vw, 15vw"
									loading="eager"
								/>
							</motion.div>
						) : (
							<motion.div
								key={"hoverIcon" + index}
								className="relative h-full w-full"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.5 }}
							>
								<Image
									src={urlFor(tileData.category?.hoverIcon).url()}
									fill
									style={{ objectFit: "contain" }}
									alt={(tileData.category as unknown as CaseCategory).title || ""}
									sizes="(max-width: 600px) 30vw, 15vw"
								/>
							</motion.div>
						)}
					</AnimatePresence>
				</div>
			</div>
		</Link>
	);
};

export default CaseTile;
